import { FC, useState } from 'react'
import { ReactComponent as AnnouncementIcon } from 'src/assets/svg/announcement.svg'
import { ReactComponent as ArrowUpIcon } from 'src/assets/svg/ri-arrow-up-circle-fill.svg'
import { Skeleton } from 'src/components/Skeleton'
import { TrendBadge } from 'src/components/TrendBadge'
import { useGetOperatorAnalyticsQuery } from 'src/reactQuery/useGetOperatorAnalyticsQuery'
import { OperatorCard } from './OperatorCard'
import classes from './operatorAnalytics.module.scss'

// .statValue is 20px/normal (operatorAnalytics.module.scss), so the
// placeholder fills the same line box inside the fixed 88px .statCard.
const STAT_VALUE_HEIGHT = 20
// Cards shown before "View all": one row of three.
const COLLAPSED_COUNT = 3

type ResponsePeriod = 'overall' | 'morning' | 'evening'

const PERIOD_FILTERS: { key: ResponsePeriod; label: string }[] = [
  { key: 'overall', label: 'Overall' },
  { key: 'morning', label: 'Response Time 09:00 - 19:00' },
  { key: 'evening', label: 'Response Time 19:00 - 09:00' },
]

const PLATFORM_OPTIONS = [
  { id: 'all', label: 'All Platforms' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'booking', label: 'Booking' },
  { id: 'expedia', label: 'Expedia' },
] as const

interface OperatorAnalyticsProps {
  // Platform ids that have data; tabs are filtered to these.
  availablePlatforms?: string[]
  fromDate?: string
  pageId: string
  toDate?: string
}

export const OperatorAnalytics: FC<OperatorAnalyticsProps> = ({
  availablePlatforms,
  fromDate,
  pageId,
  toDate,
}) => {
  const [activePlatform, setActivePlatform] = useState<string>('all')
  const [activePeriod, setActivePeriod] = useState<ResponsePeriod>('overall')
  const [isExpanded, setIsExpanded] = useState(false)

  const { data, isFetching, isLoading } = useGetOperatorAnalyticsQuery({
    fromDate,
    pageId,
    period: activePeriod,
    platform: activePlatform,
    toDate,
  })

  // First load only; a refetch keeps the previous values and dims them.
  const isInitialLoading = isLoading && !data
  const isRefetching = isFetching && !isInitialLoading

  const platformOptions = PLATFORM_OPTIONS.filter(
    (platform) =>
      platform.id === 'all' || (availablePlatforms ?? []).includes(platform.id)
  )
  const operators = data?.operators ?? []
  const stats = data?.stats
  const visibleOperators = isExpanded
    ? operators
    : operators.slice(0, COLLAPSED_COUNT)
  const hasMore = operators.length > COLLAPSED_COUNT

  const renderStatValue = (value: string, skeletonWidth: number) =>
    isInitialLoading ? (
      <Skeleton height={STAT_VALUE_HEIGHT} width={skeletonWidth} />
    ) : (
      value
    )

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div>
          <h2 className={classes.title}>Operator Analytics</h2>
          <p className={classes.subtitle}>
            Individual performance, sentiment and responsiveness per operator
          </p>
        </div>
        <div className={classes.platformTabs}>
          {platformOptions.map((platform) => (
            <button
              className={`${classes.platformTab} ${
                activePlatform === platform.id ? classes.activePlatform : ''
              }`}
              key={platform.id}
              onClick={() => setActivePlatform(platform.id)}
              type="button"
            >
              {platform.label}
            </button>
          ))}
        </div>
      </div>

      <div className={classes.periodFilters}>
        {PERIOD_FILTERS.map(({ key, label }) => (
          <button
            className={`${classes.periodPill} ${
              activePeriod === key ? classes.periodPillActive : ''
            }`}
            key={key}
            onClick={() => setActivePeriod(key)}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      <div className={isRefetching ? classes.contentFetching : undefined}>
        <div className={classes.statsRow}>
          <div className={classes.statCard}>
            <div className={classes.statIcon}>
              <AnnouncementIcon />
            </div>
            <div className={classes.statContent}>
              <div className={classes.statLabel}>Total Operators</div>
              <div className={classes.statValue}>
                {renderStatValue(String(stats?.totalOperators ?? 0), 40)}
              </div>
            </div>
          </div>

          <div className={classes.statCard}>
            <div className={classes.statIcon}>
              <AnnouncementIcon />
            </div>
            <div className={classes.statContent}>
              <div className={classes.statLabel}>Average Response Time</div>
              <div className={classes.statValueRow}>
                <div className={classes.statValue}>
                  {renderStatValue(
                    `${stats?.averageResponseTime ?? 0} Min.`,
                    72
                  )}
                </div>
                {/* Slower replies are the bad direction: a rise is red. */}
                <TrendBadge
                  lowerIsBetter
                  previousFromDate={
                    stats?.averageResponseTimeComparison?.previousFromDate
                  }
                  previousToDate={
                    stats?.averageResponseTimeComparison?.previousToDate
                  }
                  value={stats?.averageResponseTimeComparison?.changePercent}
                />
              </div>
            </div>
          </div>

          <div className={classes.statCard}>
            <div className={classes.statIcon}>
              <ArrowUpIcon />
            </div>
            <div className={classes.statContent}>
              <div className={classes.statLabel}>Top Performer</div>
              <div className={classes.statValue}>
                {renderStatValue(stats?.topPerformer?.name ?? '–', 120)}
              </div>
            </div>
          </div>

          <div className={classes.statCard}>
            <div className={classes.statIcon}>
              <ArrowUpIcon style={{ transform: 'rotate(180deg)' }} />
            </div>
            <div className={classes.statContent}>
              <div className={classes.statLabel}>Needs Attention</div>
              <div className={classes.statValue}>
                {renderStatValue(stats?.needsAttention?.name ?? '–', 120)}
              </div>
            </div>
          </div>
        </div>

        {!isInitialLoading && operators.length === 0 ? (
          <p className={classes.emptyText}>
            No operator activity for the selected period.
          </p>
        ) : (
          <div
            className={`${classes.cardsArea} ${
              hasMore && !isExpanded ? classes.collapsed : ''
            }`}
          >
            <div className={classes.cardsGrid}>
              {isInitialLoading
                ? Array.from({ length: COLLAPSED_COUNT }).map((_, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Skeleton height={420} key={index} radius={16} />
                  ))
                : visibleOperators.map((operator) => (
                    <OperatorCard key={operator.id} operator={operator} />
                  ))}
            </div>
            {hasMore && (
              <button
                className={classes.viewAllButton}
                onClick={() => setIsExpanded((expanded) => !expanded)}
                type="button"
              >
                {isExpanded
                  ? 'Show less'
                  : `View all ${operators.length} operators`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
